import * as React from 'react';

import { faChevronRight } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../../lib/utils';
import { Checkbox } from '../checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';

import type { VariableChipProps } from './types';
import {
  REQUIRED_EMPTY_PLACEHOLDER,
  formatMultiValue,
  parseMultiValue,
  valueForDisplay,
} from './utils';

/**
 * VariableChip - An inline chip that displays a variable's current value.
 * Clicking the chip opens a dropdown to select from all possible values.
 * Always supports multiple selection with comma-separated display.
 */
export const VariableChip = React.forwardRef<
  HTMLSpanElement,
  VariableChipProps
>(
  (
    {
      varName,
      currentValue,
      allValues,
      customizeFieldValuesLabel = 'Customize Field Values',
      isDisabled = false,
      onCustomizeFieldValues,
      onValueChange,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const selectedSet = React.useMemo(
      () => parseMultiValue(currentValue, allValues),
      [currentValue, allValues],
    );

    const handleSelect = (value: string) => {
      const next = new Set(selectedSet);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      onValueChange(varName, formatMultiValue(next, allValues));
    };

    const isLastSelected = (value: string) =>
      selectedSet.has(value) && selectedSet.size === 1;

    const handleCustomizeClick = () => {
      onCustomizeFieldValues?.();
      setIsOpen(false);
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild disabled={isDisabled}>
          <span
            ref={ref}
            data-variable={varName}
            contentEditable={false}
            className={cn(
              'vai-inline vai-px-1 vai-cursor-pointer vai-select-none vai-transition-colors',
              currentValue === REQUIRED_EMPTY_PLACEHOLDER
                ? 'vai-bg-red-50 vai-text-red-600 vai-font-semibold hover:vai-bg-red-100'
                : 'vai-bg-orange-100 vai-text-orange-600 hover:vai-bg-orange-200',
              isDisabled && 'vai-opacity-50 vai-cursor-not-allowed',
            )}
          >
            {valueForDisplay(currentValue)}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="vai-min-w-[150px]"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {allValues.map((value) => (
            <DropdownMenuItem
              key={value}
              onClick={() => !isLastSelected(value) && handleSelect(value)}
              onSelect={(e) => e.preventDefault()}
              className="vai-flex vai-items-center vai-gap-2"
            >
              <Checkbox
                checked={selectedSet.has(value)}
                disabled={isLastSelected(value)}
              />
              <span>{value}</span>
            </DropdownMenuItem>
          ))}
          {onCustomizeFieldValues && (
            <>
              <div className="vai-my-1 vai-h-px vai-bg-border" />
              <button
                type="button"
                onClick={handleCustomizeClick}
                className="vai-w-full vai-flex vai-items-center vai-gap-2 vai-px-2 vai-py-1.5 vai-text-sm vai-text-primary hover:vai-bg-accent hover:vai-text-accent-foreground vai-cursor-pointer vai-text-left vai-border-0 vai-bg-transparent"
              >
                <span className="vai-flex-1">{customizeFieldValuesLabel}</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="vai-h-4 vai-w-4 vai-shrink-0"
                />
              </button>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
